import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Propriedades do `vivest-radio` (design system) usadas pela integração com formulários. */
type VivestRadioElement = HTMLElement & { checked: boolean; disabled: boolean; value: string };

/**
 * Liga cada `<vivest-radio>` do design system a um controle de formulário do Angular. Todos os
 * radios de um grupo usam o mesmo `formControlName` e um `value` próprio: o marcado é o que tem o
 * valor do controle, e marcar um radio grava o `value` dele no controle.
 *
 * @example
 * <vivest-radio name="regra" value="Fixo" formControlName="regra">Percentual fixo</vivest-radio>
 * <vivest-radio name="regra" value="Tempo" formControlName="regra">Conforme tempo de plano</vivest-radio>
 */
@Directive({
  selector: 'vivest-radio[formControlName], vivest-radio[formControl], vivest-radio[ngModel]',
  standalone: true,
  host: {
    '(change)': 'aoAlterar()',
    '(focusout)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestRadioValueAccessorDirective),
      multi: true,
    },
  ],
})
export class VivestRadioValueAccessorDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<VivestRadioElement>>(ElementRef).nativeElement;

  private onChange: (valor: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: string | null): void {
    this.elemento.checked = valor === this.elemento.value;
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoAlterar(): void {
    if (this.elemento.checked) {
      this.onChange(this.elemento.value);
    }
  }

  aoSair(): void {
    this.onTouched();
  }
}
